import { ChevronDown, type LucideIcon } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface FormSectionHeaderProps {
  icon?: LucideIcon;
  iconNode?: ReactNode;
  title: string;
  description?: ReactNode;
  open?: boolean;
  onToggle?: () => void;
  extra?: ReactNode;
  statusBadge?: ReactNode;
  alwaysOpen?: boolean;
  className?: string;
}

export function FormSectionHeader({
  icon: Icon,
  iconNode,
  title,
  description,
  open = true,
  onToggle,
  extra,
  statusBadge,
  alwaysOpen = false,
  className,
}: FormSectionHeaderProps) {
  const interactive = Boolean(onToggle) && !alwaysOpen;
  const renderedIcon =
    iconNode ?? (Icon ? <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> : null);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!interactive) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle?.();
    }
  }

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onToggle : undefined}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex w-full select-none items-start justify-between gap-3',
        interactive && 'cursor-pointer',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        {renderedIcon}
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
            {statusBadge ? (
              <span className="rounded-full bg-success/10 px-1.5 py-px text-[10px] font-semibold text-success">
                {statusBadge}
              </span>
            ) : null}
          </div>
          {description ? (
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {extra ? <div onClick={(event) => event.stopPropagation()}>{extra}</div> : null}
        {interactive ? (
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-150',
              !open && '-rotate-90',
            )}
          />
        ) : null}
      </div>
    </div>
  );
}
